import { setDrofusEndringerRoomChangesPage } from "../DrofusEndringer/drofusEndringerSlice";
import {
  selectDrofusEndringerSelectedRoomApiArg,
  selectDrofusEndringerRoomChangesPage,
  selectDrofusEndringerSelectedRoomSummaryApiArg,
} from "../DrofusEndringer/drofusEndringerSelectors";
import {
  Badge,
  Box,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Pagination,
  Loader,
  Button,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { openapi } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  DrofusForekomstDetailsModal,
  type DrofusForekomstDetailsRef,
} from "../DrofusForekomster/DrofusForekomstDetailsModal";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";
import {
  selectTableRoomDrofusChangeRows,
  selectTableRoomDrofusChangeSummary,
  type IRoomDrofusChangeRow,
  type IRoomDrofusChangeSummary,
} from "./roomDrofusChangesSelectors";

const disciplineColorById: Record<string, string> = {
  RIE: "#f59f00",
  RIKT: "#1971c2",
  RIAut: "#9c36b5",
  RIV: "#0c8599",
};

export const RoomDrofusChangesPanel: React.FC = () => {
  const apiArg = useAppSelector(selectDrofusEndringerSelectedRoomApiArg);
  const summaryArg = useAppSelector(selectDrofusEndringerSelectedRoomSummaryApiArg);
  const dispatch = useAppDispatch();
  const page = useAppSelector(selectDrofusEndringerRoomChangesPage);
  const { currentData, rows, isFetching, isError, refetch } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsQuery(apiArg ?? skipToken, {
      selectFromResult: (result) => ({ ...result, rows: selectTableRoomDrofusChangeRows(result) }),
    });
  const summaryQuery = openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsByRoomQuery(
    summaryArg ?? skipToken,
    {
      selectFromResult: (result) => ({
        ...result,
        summary: selectTableRoomDrofusChangeSummary(result),
      }),
    }
  );
  return (
    <RoomChangesView
      rows={rows}
      summary={summaryQuery.summary}
      isFetching={isFetching || summaryQuery.isFetching}
      isError={isError || summaryQuery.isError}
      footer={
        <>
          {(isError || summaryQuery.isError) && (
            <Button
              size="xs"
              onClick={() => {
                if (apiArg) void refetch();
                if (summaryArg) void summaryQuery.refetch();
              }}
            >
              Prøv igjen
            </Button>
          )}
          {(currentData?.total ?? 0) > 100 && (
            <Pagination
              size="sm"
              total={Math.ceil(currentData!.total / 100)}
              value={page}
              onChange={(page) => dispatch(setDrofusEndringerRoomChangesPage(page))}
              disabled={isFetching}
            />
          )}
        </>
      }
    />
  );
};

interface IRoomChangesViewProps {
  rows: IRoomDrofusChangeRow[];
  summary: IRoomDrofusChangeSummary;
  isFetching: boolean;
  isError: boolean;
  footer?: React.ReactNode;
}
const RoomChangesView: React.FC<IRoomChangesViewProps> = ({
  rows,
  summary,
  isFetching,
  isError,
  footer,
}) => {
  const [detailsRef, setDetailsRef] = useState<DrofusForekomstDetailsRef | null>(null);
  const room = useAppSelector(selectHttpSingleRoomFull);
  return (
    <Stack gap="md" p="md" h="100%" style={{ background: "#ffffff", boxSizing: "border-box" }}>
      <DrofusForekomstDetailsModal
        occurrenceRef={detailsRef}
        opened={detailsRef != null}
        onClose={() => setDetailsRef(null)}
      />

      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box>
          <Title order={4}>Romendringer</Title>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {room?.roomNumber}, {room?.roomName}
          </Text>
        </Box>
        <Badge variant="light" color="blue">
          dRofus
        </Badge>
      </Group>

      <Group grow gap="xs">
        <SummaryBadge label="Endret" value={summary.updated} color="#f59f00" textColor="#1f2937" />
        <SummaryBadge label="Ny" value={summary.added} color="#2f9e44" textColor="#ffffff" />
        <SummaryBadge label="Fjernet" value={summary.removed} color="#e03131" textColor="#ffffff" />
      </Group>

      {isError && (
        <Paper withBorder radius="sm" p="sm" bg="red.0">
          <Text size="sm" c="red.8" fw={600}>
            Kunne ikke hente dRofus-endringer for rommet.
          </Text>
        </Paper>
      )}

      {isFetching && (
        <Group gap="xs" role="status">
          <Loader size="sm" />
          <Text size="sm">Laster endringer…</Text>
        </Group>
      )}

      {!isError && !isFetching && rows.length === 0 && (
        <Paper withBorder radius="sm" p="md" bg="gray.0">
          <Text size="sm" c="dimmed">
            {isFetching ? "Laster endringer..." : "Ingen endringer for valgt rom og filter."}
          </Text>
        </Paper>
      )}

      <Stack gap="sm" style={{ minHeight: 0, overflowY: "auto" }}>
        {rows.map((row) => (
          <Paper
            key={row.id}
            withBorder
            radius="sm"
            p="sm"
            component={row.occurrenceRef ? "button" : "div"}
            onClick={row.occurrenceRef ? () => setDetailsRef(row.occurrenceRef) : undefined}
            style={{
              borderLeft: `5px solid ${row.color}`,
              flexShrink: 0,
              cursor: row.occurrenceRef ? "pointer" : "default",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Stack gap={6}>
              <Group justify="space-between" gap="xs" align="flex-start">
                <Text size="sm" fw={700} style={{ overflowWrap: "anywhere", flex: "1 1 200px" }}>
                  {row.title}
                </Text>
                {row.disciplines.length > 0 && (
                  <Group gap={4}>
                    {row.disciplines.map((discipline) => (
                      <DisciplineBadge key={discipline} discipline={discipline} />
                    ))}
                  </Group>
                )}
              </Group>

              <Stack gap={2}>
                {row.occurrenceRef && (
                  <Text size="xs" c="dark.6">
                    Forekomst-ID: {row.occurrenceRef.occurrenceId}
                  </Text>
                )}
                <Text size="xs" c="dark.6">
                  Oppdaget: {row.detectedAtLabel}
                </Text>
              </Stack>

              <Text size="xs" c="dimmed" fw={600}>
                {row.fieldLabel}
              </Text>

              <Stack gap="xs">
                <ChangeValue label="Gammel verdi" value={row.oldValue} color="red" />
                <ChangeValue label="Ny verdi" value={row.newValue} color="green" />
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
      {footer}
    </Stack>
  );
};

const SummaryBadge: React.FC<{
  label: string;
  value: number;
  color: string;
  textColor: string;
}> = ({ label, value, color, textColor }) => (
  <Paper
    radius="sm"
    p="xs"
    style={{
      background: color,
      color: textColor,
      textAlign: "center",
    }}
  >
    <Text size="xs" fw={700}>
      {label}
    </Text>
    <Text size="lg" fw={800} lh={1.1}>
      {value}
    </Text>
  </Paper>
);

const DisciplineBadge: React.FC<{ discipline: string }> = ({ discipline }) => {
  const color = disciplineColorById[discipline] ?? "#868e96";

  return (
    <Badge
      radius="sm"
      styles={{
        root: {
          backgroundColor: "#ffffff",
          border: `1px solid ${color}`,
          color,
          flexShrink: 0,
        },
        label: { color: "inherit" },
      }}
    >
      {discipline}
    </Badge>
  );
};

interface IChangeValueProps {
  label: string;
  value: string;
  color: "red" | "green";
}
const ChangeValue: React.FC<IChangeValueProps> = ({ label, value, color }) => (
  <Box
    p="sm"
    bg={`${color}.0`}
    style={{ minWidth: 0, border: `1px solid var(--mantine-color-${color}-2)`, borderRadius: 6 }}
  >
    <Text size="xs" fw={700} c={`${color}.9`} mb={4}>
      {label}
    </Text>
    <Text size="sm" c="dark.9" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {value}
    </Text>
  </Box>
);
